import axios from "axios";

/**
 * Maintenance Opportunities API client
 * (js_crm_backend docs/leads/MAINTENANCE_OPPORTUNITY_PHASES.md).
 *
 * A completed install whose mulch is coming due, worked to a lead and a
 * proposal. Every route 403s unless the company has BOTH the leads module
 * and MAINTENANCE_OPPORTUNITIES_ENABLED on, except the settings and the
 * per-client mode routes - `getMoSettings()` is how the nav finds out.
 */

export type MoStage =
  | "candidate"
  | "identified"
  | "confirmed"
  | "lead_created"
  | "proposal_sent"
  | "won"
  | "lost"
  | "client_upsell"
  | "dismissed";

export type MoBucket =
  | "open"
  | "overdue"
  | "due_soon"
  | "later"
  | "client"
  | "leads"
  | "proposal_sent"
  | "won"
  | "lost"
  | "dismissed"
  | "all";

export type MoNextStep = "identify" | "create_lead" | "create_proposal" | "send_proposal" | "finish_proposal" | "follow_up" | "client" | "none";

/** The latest proposal for an opportunity. "ready" = link made, not sent yet. */
export type MoProposalLabel = "draft" | "ready" | "sent" | "viewed" | "accepted" | "lost";
export interface MoProposalStatus {
  id: string;
  status: MoProposalLabel;
  shareUrl: string | null;
  password: string | null;
  sentAt: string | null;
  sentTo: string | null;
  viewCount: number;
  decidedAt: string | null;
  createdAt: string;
  /** The suggested email (Mission Control or a person). */
  emailDraft: { subject: string; message: string; to: string | null; cc: string[]; by: string; at: string } | null;
}

export interface MoGenerateResult {
  opportunityId: string;
  proposalId: string | null;
  shareUrl: string | null;
  password: string | null;
  expiresAt: null;
  status: MoProposalLabel | null;
  created: boolean;
  reason?: "exists" | "already_sent" | "draft_in_wizard" | "no_end_customer" | "no_coordinates" | "no_covering_depot" | "not_open" | "error";
  message?: string;
  prospectName?: string | null;
  recipient?: { name: string | null; email: string | null } | null;
}

export type MoDismissReason = "builder_still_owns" | "not_a_playground" | "out_of_area" | "competitor" | "already_renewed" | "other";

export const MO_DISMISS_LABELS: Record<MoDismissReason, string> = {
  builder_still_owns: "Builder still owns the site",
  not_a_playground: "Not a playground / not our product",
  out_of_area: "Out of area",
  competitor: "Went with a competitor",
  already_renewed: "Already topped up elsewhere",
  other: "Other",
};

export type MaintenanceMode = "end_customer" | "client_upsell" | "managed";
export const MAINTENANCE_MODE_LABELS: Record<MaintenanceMode, string> = {
  end_customer: "Find the playground owner (builder)",
  client_upsell: "They own the sites (offer them a plan)",
  managed: "They manage their sites (keep out of the queue)",
};

export interface MoContact {
  key: string;
  kind?: "organization" | "person" | "email";
  source: string;
  name: string | null;
  type?: string | null;
  contact?: string | null;
  title?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  confidence?: "high" | "medium" | "low" | null;
  reason: string;
  selected?: boolean;
  leadId?: string | null;
  rejectedAt?: string | null;
  at: string;
}

export interface MoRow {
  id: string;
  stage: MoStage;
  nextStep: MoNextStep;
  site: {
    locationId: string;
    name: string | null;
    projectName: string | null;
    projectId: string | null;
    city: string | null;
    state: string | null;
    line1: string | null;
    latitude: number | null;
    longitude: number | null;
  };
  installedAt: string | null;
  yards: number | null;
  product: string | null;
  installer: { id: string; name: string; category: string | null } | null;
  dueAt: string | null;
  dueInDays: number | null;
  intervalMonths: number | null;
  endCustomer: {
    name: string | null;
    type: string | null;
    contact: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    source: string | null;
    confidence: string | null;
    note: string | null;
    identifiedAt: string | null;
  } | null;
  trail: MoContact[];
  rfqContact: { name: string | null; email: string | null; phone: string | null } | null;
  identifyCached: boolean;
  contactRoute: "direct" | "via_installer" | null;
  lead: { id: string; name: string; status: string | null; nextActionAt: string | null; nextActionNote: string | null } | null;
  client: { id: string; name: string } | null;
  owner: { id: string; name: string } | null;
  dismissReason: MoDismissReason | null;
  dismissNote: string | null;
  lastActivityAt: string | null;
  proposal?: MoProposalStatus | null;
  /** A depot covers the site; false = it cannot be priced. */
  priceable?: boolean | null;
  /** Per-person sends for this site (one link each). */
  sends?: { sent: number; opened: number; won: boolean };
  /** How we talk about this site (backend mo-story.ts). */
  story?: {
    band: "coming_due" | "due" | "overdue" | "long";
    monthsSince: number | null;
    installedLabel: string | null;
    /** overdue / long: the first top-up is likely a larger one (was depthCheck; we no longer offer one, 2026-11-03). */
    restoreLikely: boolean;
    hiredBy: "builder" | "owner" | "unknown";
    /** In effect: one playground or several locations (a parks dept, a district). */
    ownerScope: "single" | "multi";
    /** What a person saved; null = ownerScope is inferred. */
    ownerScopeSaved: "single" | "multi" | null;
    defaultEmail: { subject: string; message: string };
  };
  /** The saved first email for this site (Mission Control or a person). */
  emailDraft?: { subject: string; message: string; by: "mission_control" | "user"; at: string; sentAt?: string | null; version: number } | null;
}

export interface MoSettings {
  enabled: boolean;
  intervalMonths: number;
  horizonDays: number;
  defaultOwnerId: string | null;
}

export interface MoCounts {
  overdue: number;
  dueSoon: number;
  later: number;
  unknown: number;
  identified: number;
  confirmed: number;
  clientUpsells: number;
  leads: number;
  proposalSent: number;
  won: number;
  lost: number;
  dismissed: number;
  open: number;
  lastDetectAt: string | null;
  settings: MoSettings;
}

export interface MoEstimate {
  total: number;
  cached: number;
  billable: number;
  estimatedUsd: number;
  remainingToday: number;
  unsearchable: number;
  configured: boolean;
}

type Err = { error: string };

const fail = (e: any, what: string): Err => {
  const data = e?.response?.data;
  const detail = data?.message || data?.error;
  if (Array.isArray(detail)) return { error: detail.join(", ") };
  if (detail) return { error: String(detail) };
  if (e?.response?.status) return { error: `${what} failed (${e.response.status}).` };
  return { error: `${what} failed.` };
};

const BASE = "/maintenance-opportunities";

/** Off unless the call succeeds and says so. Used by the nav. */
export const getMoSettings = async (): Promise<{ settings: MoSettings; owners: Array<{ id: string; name: string }> } | Err> => {
  try {
    const res = await axios.get(`${BASE}/settings`, { excludeInterceptor: true } as any);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading Opportunities settings");
  }
};

export const updateMoSettings = async (input: Partial<MoSettings>): Promise<MoSettings | Err> => {
  try {
    const res = await axios.put(`${BASE}/settings`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Saving Opportunities settings");
  }
};

/** TableData fetcher: maps the API's { items, total, pageNum, recordsPer } to TableData's { items, total, page, pages }. */
export const getMoTable = async ({ query = {}, controller = null }: any) => {
  try {
    const res: any = await axios.get(BASE, {
      params: query,
      ...(controller ? { signal: controller.signal } : {}),
    } as any);
    const d = res?.data ?? {};
    const per = Number(d.recordsPer) || 1;
    return { items: d.items ?? [], total: d.total ?? 0, page: d.pageNum ?? 1, pages: Math.max(1, Math.ceil((d.total ?? 0) / per)) };
  } catch {
    return { items: [], total: 0, page: 1, pages: 0 };
  }
};

export const getMoCounts = async (): Promise<MoCounts | Err> => {
  try {
    const res = await axios.get(`${BASE}/counts`, { excludeInterceptor: true } as any);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading counts");
  }
};

export const getMo = async (id: string): Promise<MoRow | Err> => {
  try {
    const res = await axios.get(`${BASE}/${id}`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading the opportunity");
  }
};

export const updateMo = async (id: string, input: Record<string, any>): Promise<MoRow | Err> => {
  try {
    const res = await axios.put(`${BASE}/${id}`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Saving");
  }
};

export const detectMo = async (): Promise<any | Err> => {
  try {
    const res = await axios.post(`${BASE}/detect`);
    return res?.data ?? {};
  } catch (e: any) {
    return fail(e, "Refreshing");
  }
};

export const estimateFindContacts = async (ids: string[]): Promise<MoEstimate | Err> => {
  try {
    const res = await axios.post(`${BASE}/identify/estimate`, { ids });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Estimating");
  }
};

export const findContacts = async (ids: string[], force = false): Promise<any | Err> => {
  try {
    const res = await axios.post(`${BASE}/identify`, { ids, ...(force ? { force: true } : {}) });
    return res?.data ?? {};
  } catch (e: any) {
    // API Gateway gives up at ~29 s (503/504) while the server finishes and
    // saves: the caller waits for the saved result instead of failing.
    const status = e?.response?.status;
    if ([502, 503, 504].includes(status) || !e?.response) return { pending: true };
    return fail(e, "Finding contacts");
  }
};

export const selectMoContact = async (id: string, key: string, selected: boolean): Promise<MoRow | Err> => {
  try {
    const res = await axios.put(`${BASE}/${id}/contacts/${key}`, { selected });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Updating the contact");
  }
};

export const addMoContact = async (id: string, input: { contact?: string; email?: string; phone?: string; title?: string }): Promise<MoRow | Err> => {
  try {
    const res = await axios.post(`${BASE}/${id}/contacts`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Adding the contact");
  }
};

export const notThemMo = async (id: string): Promise<MoRow | Err> => {
  try {
    const res = await axios.post(`${BASE}/${id}/not-them`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Rejecting");
  }
};

export const notClientMo = async (id: string): Promise<MoRow | Err> => {
  try {
    const res = await axios.post(`${BASE}/${id}/not-client`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Moving to the queue");
  }
};

export const createMoLead = async (id: string): Promise<any | Err> => {
  try {
    const res = await axios.post(`${BASE}/${id}/create-lead`, {});
    return res?.data ?? {};
  } catch (e: any) {
    return fail(e, "Creating the lead");
  }
};

/** Proposals with links for these opportunities. Nothing is emailed. Max 50 per call. */
export const generateMoProposals = async (ids: string[], opts: { requirePassword?: boolean; force?: boolean } = {}): Promise<{ results: MoGenerateResult[]; created: number; existing: number; skipped: number } | Err> => {
  try {
    const res = await axios.post(`${BASE}/proposals`, { ids, ...opts });
    return res?.data ?? { results: [], created: 0, existing: 0, skipped: 0 };
  } catch (e: any) {
    return fail(e, "Generating proposals");
  }
};

export interface ServiceToken {
  id: string;
  name: string;
  tokenPrefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export const listServiceTokens = async (): Promise<ServiceToken[] | Err> => {
  try {
    const res = await axios.get(`/service-tokens`);
    return Array.isArray(res?.data) ? res.data : [];
  } catch (e: any) {
    return fail(e, "Loading tokens");
  }
};

export const createServiceToken = async (name: string): Promise<{ id: string; token: string } | Err> => {
  try {
    const res = await axios.post(`/service-tokens`, { name });
    return res?.data;
  } catch (e: any) {
    return fail(e, "Creating the token");
  }
};

export const revokeServiceToken = async (id: string): Promise<{ revoked: boolean } | Err> => {
  try {
    const res = await axios.delete(`/service-tokens/${id}`);
    return res?.data;
  } catch (e: any) {
    return fail(e, "Revoking the token");
  }
};

/** A person checked the suggested owner: identified -> confirmed. */
export const confirmMo = async (id: string): Promise<MoRow | Err> => {
  try {
    const res = await axios.post(`${BASE}/${id}/confirm`, {});
    return res?.data;
  } catch (e: any) {
    return fail(e, "Confirming");
  }
};

/** Review & send one site: one email, one proposal link and one lead per person. */
export const sendMoSite = async (
  id: string,
  input: { recipients: string[]; subject: string; message: string; includePassword?: boolean; ownerName?: string | null; senderUserId?: string },
): Promise<{ results: Array<{ key: string; email: string | null; name: string | null; ok: boolean; error?: string; proposalId: string | null; leadId: string | null }>; sent: number } | Err> => {
  try {
    const res = await axios.post(`${BASE}/${id}/send`, input);
    return res?.data;
  } catch (e: any) {
    return fail(e, "Sending");
  }
};

/** Can the app read my mailbox (reply tracking)? */
export const checkMailboxAccess = async (): Promise<{ mailbox: string; canRead: boolean; error: string | null } | Err> => {
  try {
    const res = await axios.get(`/service-tokens/mailbox-check`);
    return res?.data;
  } catch (e: any) {
    return fail(e, "Checking mailbox access");
  }
};

export const createMoLeads = async (ids: string[]): Promise<any | Err> => {
  try {
    const res = await axios.post(`${BASE}/create-leads`, { ids });
    return res?.data ?? {};
  } catch (e: any) {
    return fail(e, "Creating leads");
  }
};

export const dismissMo = async (ids: string[], reason: MoDismissReason, note?: string): Promise<any | Err> => {
  try {
    const res = await axios.post(`${BASE}/dismiss`, { ids, reason, note });
    return res?.data ?? {};
  } catch (e: any) {
    return fail(e, "Dismissing");
  }
};

export const reopenMo = async (id: string): Promise<MoRow | Err> => {
  try {
    const res = await axios.post(`${BASE}/${id}/reopen`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Reopening");
  }
};

export const getMoProposalPrefill = async (id: string): Promise<any | Err> => {
  try {
    const res = await axios.get(`${BASE}/${id}/proposal-prefill`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading the proposal prefill");
  }
};

/** Sent proposals still carrying the old depth-check wording: dryRun counts, otherwise rewrites (same link, prices unchanged). */
export const refreshMoProposalCopy = async (dryRun: boolean): Promise<{ matched: number; changed: number } | Err> => {
  try {
    const res = await axios.post(`${BASE}/proposals/refresh-copy`, null, { params: { dryRun: dryRun ? 1 : 0 } });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Updating proposal wording");
  }
};

/** Combine: several opportunities -> one multi-site proposal. */
export const getMoProposalPrefillMany = async (ids: string[]): Promise<any | Err> => {
  try {
    const res = await axios.get(`${BASE}/proposal-prefill`, { params: { ids: ids.join(",") } });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading the sites to combine");
  }
};

/** Combine & send: one proposal for several sites, one email + link + lead per person picked ("oppId::key"). */
/** Combine preview: build the combined proposal (attached to nothing until Send). `order` = primary first. */
export const prepareMoCombined = async (ids: string[], ownerName: string | null): Promise<{ proposalId: string; shareUrl: string; password: string | null; order: string[]; prospectName: string | null } | Err> => {
  try {
    const res = await axios.post(`${BASE}/combine/prepare`, { ids, ownerName });
    return res?.data;
  } catch (e: any) {
    return fail(e, "Building the combined proposal");
  }
};

export const sendMoCombined = async (input: { ids: string[]; recipients: string[]; subject: string; message: string; includePassword?: boolean; ownerName?: string | null; senderUserId?: string; preparedId?: string | null }): Promise<{ sent: number; proposalId: string; results: Array<{ email: string | null; ok: boolean; error?: string }> } | Err> => {
  try {
    const res = await axios.post(`${BASE}/combine/send`, input);
    return res?.data;
  } catch (e: any) {
    return fail(e, "Sending the combined proposal");
  }
};

/** People found on the opportunities a proposal covers. */
export const getMoProposalContacts = async (proposalId: string): Promise<{ contacts: Array<{ email: string; contact: string | null; title: string | null; site: string | null }> } | Err> => {
  try {
    const res = await axios.get(`${BASE}/proposal-contacts/${proposalId}`);
    return res?.data ?? { contacts: [] };
  } catch (e: any) {
    return fail(e, "Loading the site contacts");
  }
};

export const getMoForProject = async (projectId: string): Promise<{ opportunity: MoRow | null; reason: string | null } | Err> => {
  try {
    const res = await axios.get(`${BASE}/projects/${projectId}`, { excludeInterceptor: true } as any);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading the opportunity");
  }
};

export const createMoFromProject = async (projectId: string): Promise<MoRow | Err> => {
  try {
    const res = await axios.post(`${BASE}/projects/${projectId}`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Creating the opportunity");
  }
};

export const getClientMaintenanceMode = async (clientId: string): Promise<{ clientId: string; name: string; explicit: MaintenanceMode | null; effective: MaintenanceMode } | Err> => {
  try {
    const res = await axios.get(`${BASE}/clients/${clientId}/mode`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading the installer setting");
  }
};

export const setClientMaintenanceMode = async (clientId: string, mode: MaintenanceMode | null): Promise<any | Err> => {
  try {
    const res = await axios.put(`${BASE}/clients/${clientId}/mode`, { mode });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Saving the installer setting");
  }
};

/** Filter choices for the queue: installers and site states with opportunities. */
export interface MoFacets {
  installers: Array<{ id: string; name: string; total: number; open: number }>;
  states: Array<{ state: string; total: number; open: number }>;
}
export const getMoFacets = async (): Promise<MoFacets> => {
  try {
    const res = await axios.get(`${BASE}/facets`);
    return res?.data ?? { installers: [], states: [] };
  } catch {
    return { installers: [], states: [] };
  }
};
